import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Users, Megaphone, Trash2, BellRing, Loader2, MessageSquare, Mail } from "lucide-react";
import { V } from "@/lib/vocab";

const fmtStamp = (iso) => { try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }); } catch { return ""; } };

// Guest list + waitlist for one event, with office controls.
export function RsvpDialog({ event, tenantId, canManage, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/tenants/${tenantId}/events/${event.id}/rsvps`).then((r) => setData(r.data)).catch(() => setData({ guests: [], waitlist: [] }));
  useEffect(() => { if (event) load(); }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!event) return null;
  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.name}${row.unit ? ` (${row.unit})` : ""} from the list?`)) return;
    setBusy(row.id);
    try { const r = await api.delete(`/tenants/${tenantId}/events/${event.id}/rsvps/${row.id}`); toast.success(r.data.promoted ? `Removed — ${r.data.promoted} moved up from the waitlist` : "Removed"); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "Couldn't remove")); }
    finally { setBusy(""); }
  };
  const remind = async () => {
    setBusy("remind");
    try { const r = await api.post(`/tenants/${tenantId}/events/${event.id}/run-reminders`); toast.success(`Reminder sent to ${r.data.sent} RSVP${r.data.sent === 1 ? "" : "s"}`); }
    catch (e) { toast.error(errMsg(e, "Couldn't send")); }
    finally { setBusy(""); }
  };
  const Row = ({ r, wait }) => (
    <div data-testid={`event-rsvp-row-${r.id}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <div className="min-w-0">
        <div className="font-semibold text-ink truncate">{r.name}{r.unit ? <span className="text-ink/50 font-normal"> · {r.unit}</span> : null}</div>
        <div className="text-[11px] text-ink/50">{r.channel} · {fmtStamp(r.created_at)}{r.promoted_at ? " · moved up from waitlist" : ""}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${wait ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{wait ? "waitlist · " : ""}party of {r.party_size}</span>
        {canManage && <button data-testid={`event-rsvp-remove-${r.id}`} disabled={busy === r.id} onClick={() => remove(r)} className="text-ink/40 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="event-rsvps-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Users className="h-5 w-5 text-emerald-600" /> {event.title}</DialogTitle>
          <DialogDescription data-testid="event-rsvps-summary">
            {data ? <>{data.rsvp_count} RSVP{data.rsvp_count === 1 ? "" : "s"} · {data.people} {data.people === 1 ? "person" : "people"}{event.capacity ? ` of ${event.capacity}` : ""}{data.waitlist?.length ? ` · ${data.waitlist.length} on the waitlist` : ""}</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-ink/40" /></div> : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="rounded-xl border border-sand-200 divide-y divide-sand-100" data-testid="event-rsvps-guests">
              {data.guests.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground text-center">No RSVPs yet.</div>}
              {data.guests.map((r) => <Row key={r.id} r={r} />)}
            </div>
            {data.waitlist.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1">Waitlist — oldest first, moved up automatically</div>
                <div className="rounded-xl border border-amber-200 divide-y divide-amber-100" data-testid="event-rsvps-waitlist">{data.waitlist.map((r) => <Row key={r.id} r={r} wait />)}</div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          {canManage && data?.guests?.length > 0 && !event.is_past && (
            <button data-testid="event-rsvps-remind" disabled={busy === "remind"} onClick={remind} className="rounded-full border border-sand-200 px-4 py-2 text-sm font-semibold hover:border-ink inline-flex items-center gap-1.5"><BellRing className="h-4 w-4" /> {busy === "remind" ? "Sending…" : "Send reminder now"}</button>
          )}
          <button onClick={onClose} className="rounded-full font-semibold px-5 py-2 border border-sand-200 hover:bg-sand-50">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// "Announce this event" — pick channels, see the copy, send.
export function AnnounceDialog({ event, tenantId, settings, smsReady, onClose, onDone }) {
  const [sms, setSms] = useState(!!settings?.announce_sms && smsReady);
  const [email, setEmail] = useState(settings?.announce_email !== false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSms(!!settings?.announce_sms && smsReady); setEmail(settings?.announce_email !== false); }, [event?.id, settings, smsReady]);
  if (!event) return null;
  const send = async () => {
    const channels = [sms && "sms", email && "email"].filter(Boolean);
    if (!channels.length) { toast.error("Pick at least one channel"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/events/${event.id}/announce`, { channels });
      const st = r.data.stats || {};
      toast.success(`Announced — ${st.sms_sent || 0} text${st.sms_sent === 1 ? "" : "s"}, ${st.email_sent || 0} email${st.email_sent === 1 ? "" : "s"}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't announce")); }
    finally { setBusy(false); }
  };
  const when = (() => { try { return new Date(event.date + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" }); } catch { return event.date; } })() + (event.time ? ` at ${event.time}` : "");
  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="event-announce-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Megaphone className="h-5 w-5 text-redline-500" /> Announce “{event.title}”</DialogTitle>
          <DialogDescription>Goes to every {V.lc("person")} who hasn't muted Events under their notification settings. Texts only reach {V.lcs("person")} who opted in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <label className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${sms ? "border-ink bg-sand-50" : "border-sand-200"} ${!smsReady ? "opacity-50" : ""}`}>
              <input data-testid="event-announce-sms" type="checkbox" disabled={!smsReady} checked={sms} onChange={(e) => setSms(e.target.checked)} /> <MessageSquare className="h-4 w-4" /> Text{!smsReady ? " (no number yet)" : ""}
            </label>
            <label className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${email ? "border-ink bg-sand-50" : "border-sand-200"}`}>
              <input data-testid="event-announce-email" type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> <Mail className="h-4 w-4" /> Email
            </label>
          </div>
          <div className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 text-sm" data-testid="event-announce-preview">
            <div className="font-semibold text-ink">{event.title}</div>
            <div className="text-ink/80">{when}{event.location ? ` · ${event.location}` : ""}</div>
            {event.description && <div className="text-ink/70 mt-1 whitespace-pre-wrap">{event.description}</div>}
            {event.host_name && <div className="text-ink/70 mt-1">Hosted by {event.host_name}{event.host_contact ? ` · ${event.host_contact}` : ""}</div>}
            {event.rsvp_enabled && <div className="text-ink/70 mt-1">RSVP in the portal, or text “RSVP {event.title}”{event.capacity ? ` (${event.capacity} spots)` : ""}.</div>}
          </div>
          {event.announced_at && <div className="text-xs text-amber-700" data-testid="event-announce-again">Already announced {fmtStamp(event.announced_at)} — this sends it again.</div>}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full font-semibold px-5 py-2.5 border border-sand-200 hover:bg-sand-50">Cancel</button>
          <button data-testid="event-announce-send" disabled={busy} onClick={send} className="rounded-full font-semibold px-6 py-2.5 text-white bg-redline-500 hover:bg-redline-600 disabled:opacity-60 inline-flex items-center gap-2"><Megaphone className="h-4 w-4" /> {busy ? "Sending…" : "Send announcement"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
